import { inject, Injectable, NgZone } from '@angular/core';
import { Observable, finalize, share } from 'rxjs';
import { AuthService } from '@features/auth/services/auth-service';
import { environment } from '@env/environment';

import { logger } from '@core/utils/logger';
import { SseEvent, SseEventName } from '@api/models';

/** Eventos del canal de la mesa. Un nombre que no esté en el contrato no compila. */
const SESSION_EVENTS = [
  'new-order',
  'order-updated',
  'order-update-status',
  'payment-updated',
  'table-bill-updated',
  'bill-split-applying',
  'user-joined',
  'user-left',
  'migrated-guest-session',
  'count-updated',
  'host-delegated',
  'reassign-table',
] as const satisfies readonly SseEventName[];

/** Eventos del canal del usuario logueado. */
const USER_EVENTS = [
  'new_notification',
  'favorite-updated',
] as const satisfies readonly SseEventName[];

export type SessionSseEvent = SseEvent<(typeof SESSION_EVENTS)[number]>;
export type UserSseEvent = SseEvent<(typeof USER_EVENTS)[number]>;

/** Aviso del back al conectar: no lleva datos de negocio, sólo reinicia los reintentos. */
const CONNECTION_EVENT = 'connection-successful';

@Injectable({ providedIn: 'root' })
export class ServerSentEventsService {
  private authService = inject(AuthService);
  private zone = inject(NgZone);

  private session$?: Observable<SessionSseEvent>;
  private user$?: Observable<UserSseEvent>;

  subscribeToSession(): Observable<SessionSseEvent> {
    if (!this.session$) {
      this.session$ = this.createSseStream(
        () => `${environment.apiUrl}/events-subscriptions/table-sessions`,
        SESSION_EVENTS,
        'session',
      ).pipe(
        finalize(() => (this.session$ = undefined)),
        share({ resetOnRefCountZero: true }),
      );
    }
    return this.session$;
  }

  subscribeToUser(): Observable<UserSseEvent> {
    if (!this.user$) {
      this.user$ = this.createSseStream(
        () => `${environment.apiUrl}/events-subscriptions/users`,
        USER_EVENTS,
        'user',
      ).pipe(
        finalize(() => (this.user$ = undefined)),
        share({ resetOnRefCountZero: true }),
      );
    }
    return this.user$;
  }

  private createSseStream<N extends SseEventName>(
    urlBuilder: () => string,
    eventTypes: readonly N[],
    label: 'session' | 'user',
  ): Observable<SseEvent<N>> {
    return new Observable<SseEvent<N>>((observer) => {
      let retryDelay = 2000;
      let retryCount = 0;
      let eventSource: EventSource | null = null;
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

      const clearReconnectTimer = () => {
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
      };

      const closeEventSource = () => {
        try {
          eventSource?.close();
        } catch {
          // close() sobre un EventSource ya cerrado no debe cortar la limpieza.
        }
        eventSource = null;
      };

      const connect = () => {
        const token = this.authService.accessToken();
        if (!token) {
          clearReconnectTimer();
          observer.error(new Error(`No auth token found for SSE ${label}`));
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            connect();
          }, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000);
          return;
        }

        const clientId = this.getOrCreateClientId(label);
        const sseUrl = `${urlBuilder()}?token=${encodeURIComponent(token)}&clientId=${encodeURIComponent(clientId)}`;
        eventSource = new EventSource(sseUrl);

        eventSource.onopen = () => {
          clearReconnectTimer();
          this.zone.run(() => {
            retryDelay = 2000;
            retryCount = 0;
          });
        };

        eventSource.addEventListener(CONNECTION_EVENT, () => {
          this.zone.run(() => {
            retryDelay = 2000;
            retryCount = 0;
          });
        });

        eventTypes.forEach((eventName) => {
          eventSource!.addEventListener(eventName, (event) => {
            this.zone.run(() => {
              const data = (event as MessageEvent).data;
              try {
                // El dato tiene la forma que declara el contrato para ese evento (el back lo controla al mandarlo)
                observer.next({ type: eventName, payload: JSON.parse(data) } as SseEvent<N>);
              } catch (error) {
                logger.warn(
                  'Tiempo real',
                  `Evento con datos ilegibles: evento=${eventName}`,
                  error,
                );
              }
            });
          });
        });

        eventSource.onerror = (error) => {
          this.zone.run(() => {
            logger.warn(
              'Tiempo real',
              `Error en la conexión en tiempo real: canal=${label}`,
              error,
            );
            logger.warn('Tiempo real', 'Error en la conexión en tiempo real', {
              label,
              readyState: eventSource?.readyState,
            });

            closeEventSource();

            const stillHasToken = !!this.authService.accessToken();
            if (!stillHasToken) {
              clearReconnectTimer();
              observer.error(new Error(`SSE Auth Error (${label}): logged out`));
              return;
            }

            clearReconnectTimer();

            reconnectTimer = setTimeout(() => {
              reconnectTimer = null;

              retryCount++;
              retryDelay = Math.min(retryDelay * 2, 30000);

              if (retryCount > 10) {
                observer.error(new Error(`SSE failed after retries (${label})`));
                return;
              }
              connect();
            }, retryDelay);
          });
        };
      };

      connect();

      return () => {
        clearReconnectTimer();
        closeEventSource();
      };
    });
  }

  private getOrCreateClientId(label: string): string {
    const key = `dinno-sse-clientId-${label}`;
    let v = sessionStorage.getItem(key);
    if (!v) {
      v = crypto.randomUUID();
      sessionStorage.setItem(key, v);
    }
    return v;
  }
}
